import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";

import { fetchBenefitQuestions, fetchCoverageAnswer } from "../../utils/apiUtils";
import {
    loadDiagnosis,
    loadModifiers,
    loadPlaceOfService,
    loadProcedureCodes,
    loadRevenueCodes,
    loadServices,
    loadTypeOfBill,
} from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";

/**
 * "Ask a question" mode: HRP's own rules engine decides what is covered and how
 * much, instead of the browse tabs' reverse-engineering of provision text.
 *
 * The distinction this view exists to preserve: HRP returns a "possible match"
 * for any rule it merely could not rule out, including rules that survived only
 * because a dimension was left blank. Presenting a possible match as an answer
 * is the main way a screen like this misleads people, so exact, possible, and
 * default-provision outcomes are rendered differently.
 */

const CLAIM_TYPE_OPTIONS = [
    { value: "Professional", label: "Professional" },
    { value: "Institutional", label: "Institutional" },
    { value: "Dental", label: "Dental" },
];
const PCP_OPTIONS = [
    { value: "Rendered by PCP", label: "Rendered by PCP" },
    { value: "Not rendered by PCP", label: "Not rendered by PCP" },
    { value: "Unspecified", label: "Unspecified" },
];

const money = (value) => {
    if (value == null || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

/** Already-formatted strings come back from HRP as "$1,200.00"; leave them be. */
const balance = (value) => {
    if (value == null || value === "") return null;
    const s = String(value);
    return /[$%]/.test(s) ? s : money(s);
};

const networkLabel = (tier) => {
    if (tier.isInNetwork === true) return "In network";
    if (tier.isInNetwork === false) return "Out of network";
    return tier.tierName || "Other";
};

const sortNetworks = (tiers) => [...tiers].sort((a, b) => {
    const rank = (t) => (t.isInNetwork === true ? 0 : t.isInNetwork === false ? 1 : 2);
    return rank(a) - rank(b);
});

/** Drop the Global bucket unless it is the only real answer. */
const visibleTiers = (tiers) => {
    const network = tiers.filter((t) => t.isInNetwork === true || t.isInNetwork === false);
    const other = tiers.filter((t) => t.isInNetwork == null && (
        (t.verdict === "not-covered" && t.matchQuality === "exact")
        || (t.verdict === "covered" && !t.answeredByDefault)
    ));
    const shown = network.length ? [...network, ...other] : tiers;
    return sortNetworks(shown.filter((t) => t.verdict !== "unknown"));
};

function memberStory(tier, { askedCode } = {}) {
    if (tier.verdict === "not-covered") {
        return {
            headline: "Not covered",
            detail: "The member pays the full allowed amount on this network.",
        };
    }
    if (tier.answeredByDefault) {
        return {
            headline: "No specific benefit found",
            detail: askedCode
                ? `${askedCode} is used in more than one setting. Add a place of service to get the real benefit.`
                : "HRP only matched the plan’s catch-all rule. That is not a quote — add a service code.",
        };
    }
    if (tier.verdict === "unknown") {
        return { headline: "No rule applied", detail: "Nothing in this network matched the question." };
    }

    const cs = tier.costShare || {};
    const planPct = cs.coinsurancePercent;
    const copay = cs.copay ?? cs.benefitAmount;
    const hasCopay = copay != null && Number(copay) > 0;
    const memberPct = planPct == null ? null : Math.max(0, 100 - Number(planPct));

    let headline = "Covered";
    if (hasCopay && planPct === 100) headline = `${money(copay)} copay`;
    else if (hasCopay && planPct != null) headline = `${money(copay)} copay, then member pays ${memberPct}%`;
    else if (planPct === 100) headline = "Covered in full";
    else if (planPct != null) headline = `Member pays ${memberPct}%`;

    const bits = [];
    if (planPct != null && planPct !== 100) bits.push(`Plan pays ${planPct}%.`);
    if (cs.deductibleWaived) bits.push("Deductible is waived.");
    else if (planPct != null && planPct < 100) bits.push("Deductible applies first.");
    if (cs.label) bits.push(cs.label);

    return { headline, detail: bits.join(" ") || null };
}

function confidence(tier, { askedCode } = {}) {
    if (tier.answeredByDefault) {
        return { key: "weak", label: askedCode ? "Add a place of service" : "Add a service code" };
    }
    if (tier.matchQuality === "exact") return { key: "sure", label: "Confirmed" };
    if (tier.matchQuality === "possible") return { key: "maybe", label: "Needs more detail" };
    return { key: "none", label: "No match" };
}

function BoundList({ title, rows, showBalances }) {
    if (!rows?.length) return null;
    return (
        <div className="answer-bounds">
            <div className="answer-bounds-title">{title}</div>
            <ul className="answer-bound-list">
                {rows.map((row, i) => {
                    const amount = money(row.amount) ?? (row.units != null ? `${row.units} units` : null);
                    const level = row.level ? row.level.toLowerCase() : null;
                    return (
                        <li key={`${row.label}-${i}`}>
                            <span className="answer-bound-main">
                                {level ? `${level} ` : ""}
                                {amount || row.label || "Amount not stated"}
                                {row.interval ? ` / ${row.interval}` : ""}
                            </span>
                            {showBalances && (row.remaining || row.used) && (
                                <span className="answer-bound-left">
                                    {row.isMet ? "Met" : [balance(row.used) && `${balance(row.used)} used`, balance(row.remaining) && `${balance(row.remaining)} left`].filter(Boolean).join(" · ")}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function usefulSharpeners(tier) {
    const rows = tier.sharpeners || [];
    if (!tier.answeredByDefault) return rows;
    const pos = rows.filter((s) => /place of service/i.test(s) && !/Exclusion may apply/i.test(s));
    if (pos.length) return pos;
    return rows.filter((s) => !/Exclusion may apply|claim source|repricer/i.test(s));
}

function TierAnswerCard({ tier, showBalances, askedCode }) {
    const netClass = tier.isInNetwork === true ? "inn" : tier.isInNetwork === false ? "oon" : "";
    const story = memberStory(tier, { askedCode });
    const conf = confidence(tier, { askedCode });
    const sharpeners = usefulSharpeners(tier);
    const exactExclusions = (tier.exclusions || []).filter((ex) => ex.matchQuality === "exact");

    return (
        <div className={`prov answer-card ${netClass} verdict-${tier.verdict} quality-${tier.matchQuality}`}>
            <div className="answer-card-head">
                <div className="answer-card-network">{networkLabel(tier)}</div>
                <span className={`badge answer-quality-${conf.key === "sure" ? "exact" : conf.key === "maybe" ? "possible" : "none"}`}>
                    {conf.label}
                </span>
            </div>

            <div className="prov-answer">
                <div className="prov-headline">{story.headline}</div>
                {story.detail && <p className="answer-story">{story.detail}</p>}
            </div>

            {tier.matchQuality === "possible" && !tier.answeredByDefault && (
                <p className="answer-guidance warn">
                    This is a possible match only — do not quote it to a member until you add
                    the missing detail below.
                </p>
            )}

            {tier.requiresReview && !tier.answeredByDefault && (
                <p className="answer-guidance">A claims reviewer still has to look at this.</p>
            )}

            {tier.waitingPeriod && (
                <p className="answer-guidance">
                    Waiting period applies
                    {tier.waitingPeriod.firstEligibleDate
                        ? ` · first eligible ${tier.waitingPeriod.firstEligibleDate}`
                        : ""}.
                </p>
            )}

            {tier.authorization?.required && (
                <p className="answer-auth">
                    {tier.authorization.matchQuality === "possible"
                        ? "Prior authorization may be required."
                        : "Prior authorization is required."}
                </p>
            )}

            {!!exactExclusions.length && tier.verdict !== "not-covered" && (
                <p className="answer-guidance warn">An exclusion also matched on this network.</p>
            )}

            {(!!tier.deductible?.length || !!tier.oopMax?.length || !!tier.limits?.length) && (
                showBalances ? (
                    <>
                        <BoundList title="Deductible" rows={tier.deductible} showBalances />
                        <BoundList title="Out-of-pocket max" rows={tier.oopMax} showBalances />
                        <BoundList title="Visit or unit limits" rows={tier.limits} showBalances />
                    </>
                ) : (
                    <details className="prov-hrl">
                        <summary>Deductible, OOP, and limits</summary>
                        <BoundList title="Deductible" rows={tier.deductible} />
                        <BoundList title="Out-of-pocket max" rows={tier.oopMax} />
                        <BoundList title="Visit or unit limits" rows={tier.limits} />
                    </details>
                )
            )}

            {!!sharpeners.length && (tier.matchQuality !== "exact" || tier.answeredByDefault) && (
                <div className="answer-sharpeners">
                    <span className="answer-sharpeners-label">To confirm this, add</span>
                    <div className="chip-wrap">
                        {sharpeners.slice(0, 4).map((s, i) => (
                            <span key={i} className="chip static">{s}</span>
                        ))}
                    </div>
                </div>
            )}

            {tier.why && (
                <details className="prov-hrl">
                    <summary>Rule that won</summary>
                    <dl className="prov-summary">
                        {tier.why.componentName && (
                            <div className="prov-summary-row">
                                <dt>From</dt>
                                <dd>{tier.why.componentName}</dd>
                            </div>
                        )}
                        {tier.why.weight != null && (
                            <div className="prov-summary-row">
                                <dt>Priority</dt>
                                <dd>slot weight {tier.why.weight}</dd>
                            </div>
                        )}
                    </dl>
                    {tier.why.ruleText && <div className="prov-text">{tier.why.ruleText}</div>}
                </details>
            )}
        </div>
    );
}

const selectMenuStyles = {
    ...customStyles,
    menuPortal: (base) => ({ ...base, zIndex: 200 }),
};

const POS_LABELS = {
    "02": "02 — Telehealth",
    "10": "10 — Telehealth in home",
    "11": "11 — Office",
    "12": "12 — Home",
    "17": "17 — Walk-in retail clinic",
    "20": "20 — Urgent care",
    "22": "22 — Outpatient hospital",
    "23": "23 — Emergency room",
};

const optionFromCode = (code, labels = POS_LABELS) => (
    code ? { value: code, label: labels[code] || code } : null
);
const valuesFrom = (options) => {
    if (!options) return undefined;
    const list = Array.isArray(options) ? options : [options];
    const values = list.map((opt) => opt?.value).filter(Boolean);
    return values.length ? values : undefined;
};

const EMPTY_CRITERIA = {
    unitCount: "",
    billedAmount: "",
    practitionerHccId: "",
    supplierHccId: "",
};

function CodeSelect({
    inputId,
    value,
    onChange,
    loadOptions,
    placeholder,
    isMulti = false,
}) {
    return (
        <AsyncPaginate
            inputId={inputId}
            placeholder={placeholder}
            className="react-select-container"
            classNamePrefix="react-select"
            debounceTimeout={300}
            value={value}
            loadOptions={loadOptions}
            onChange={onChange}
            isClearable
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            styles={selectMenuStyles}
        />
    );
}

function CoverageAnswer({ plan, asOf, prefill, onPrefillConsumed, onBack, backLabel }) {
    const [questionName, setQuestionName] = useState(null);
    const [serviceCode, setServiceCode] = useState(null);
    const [placeOfService, setPlaceOfService] = useState(null);
    const [claimType, setClaimType] = useState(null);
    const [renderedByPcp, setRenderedByPcp] = useState(null);
    const [revenueCode, setRevenueCode] = useState(null);
    const [typeOfBill, setTypeOfBill] = useState(null);
    const [diagnoses, setDiagnoses] = useState([]);
    const [modifiers, setModifiers] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [memberHccId, setMemberHccId] = useState("");
    const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [fromCategory, setFromCategory] = useState(null);

    const { data: questionData, isLoading: questionsLoading } = useQuery({
        queryKey: ["benefit-questions"],
        queryFn: () => fetchBenefitQuestions({}),
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const questionOptions = useMemo(
        () => (questionData?.questions || []).map((name) => ({ value: name, label: name })),
        [questionData],
    );

    const answer = useMutation({
        mutationFn: (body) => fetchCoverageAnswer(body),
    });

    const setField = (key) => (event) =>
        setCriteria((prev) => ({ ...prev, [key]: event.target.value }));

    const runWith = ({
        question = questionName,
        service = serviceCode,
        pos = placeOfService,
        claim = claimType,
        pcp = renderedByPcp,
        rev = revenueCode,
        tob = typeOfBill,
        dx = diagnoses,
        mods = modifiers,
        procs = procedures,
        member = memberHccId,
        extra = criteria,
    } = {}) => {
        answer.mutate({
            planName: plan?.name,
            planHccId: plan?.hccId,
            asOfDate: asOf,
            questionName: question?.value || undefined,
            memberHccId: member.trim() || undefined,
            criteria: {
                serviceCode: service?.value || undefined,
                placeOfService: pos?.value || undefined,
                claimType: claim?.value || undefined,
                serviceDate: asOf,
                unitCount: extra.unitCount ? Number(extra.unitCount) : undefined,
                billedAmount: extra.billedAmount ? Number(extra.billedAmount) : undefined,
                renderedByMembersPCP: pcp?.value || undefined,
                revenueCode: rev?.value || undefined,
                typeOfBill: tob?.value || undefined,
                diagnoses: valuesFrom(dx),
                modifiers: valuesFrom(mods),
                procedures: valuesFrom(procs),
                practitionerHccId: extra.practitionerHccId.trim() || undefined,
                supplierHccId: extra.supplierHccId.trim() || undefined,
            },
        });
    };

    // Arriving from "Check this service": adopt the clicked CPT/HCPCS and ask
    // immediately — that code is what turns a possible match into an exact one.
    useEffect(() => {
        if (!prefill) return;
        const nextQuestion = prefill.questionName
            ? { value: prefill.questionName, label: prefill.questionName }
            : questionName;
        const nextService = optionFromCode(prefill.serviceCode) || serviceCode;
        const nextPos = optionFromCode(prefill.placeOfService) || placeOfService;
        const nextClaim = prefill.claimType
            ? { value: prefill.claimType, label: prefill.claimType }
            : claimType;
        if (prefill.questionName) setQuestionName(nextQuestion);
        if (prefill.serviceCode) setServiceCode(nextService);
        if (prefill.placeOfService) setPlaceOfService(nextPos);
        if (prefill.claimType) setClaimType(nextClaim);
        setFromCategory(prefill.serviceCategory || null);
        onPrefillConsumed?.();
        if (plan?.name) {
            runWith({ question: nextQuestion, service: nextService, pos: nextPos, claim: nextClaim });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefill]);

    const result = answer.data;
    const showBalances = !!result?.accumulatorsIncluded && !!result?.member;
    const askedCode = result?.question?.inputs?.serviceCode || serviceCode?.value || null;
    const tiers = visibleTiers(result?.tiers || []);
    const answerRef = useRef(null);

    useEffect(() => {
        if (!result || result.error) return;
        const target = answerRef.current;
        const host = target?.closest(".answer-host");
        if (!host || !target) return;
        const top = target.getBoundingClientRect().top - host.getBoundingClientRect().top + host.scrollTop - 8;
        host.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [result]);

    const reset = () => {
        setQuestionName(null);
        setServiceCode(null);
        setPlaceOfService(null);
        setClaimType(null);
        setRenderedByPcp(null);
        setRevenueCode(null);
        setTypeOfBill(null);
        setDiagnoses([]);
        setModifiers([]);
        setProcedures([]);
        setMemberHccId("");
        setCriteria(EMPTY_CRITERIA);
        setFromCategory(null);
        answer.reset();
    };

    return (
        <div className="answer-panel">
            <section className="overview-block">
                <header className="overview-block-head">
                    <div>
                        {onBack && (
                            <button
                                type="button"
                                className="coverage__link-btn"
                                onClick={onBack}
                                style={{ marginBottom: "0.45rem" }}
                            >
                                ← {backLabel || "Back"}
                            </button>
                        )}
                        <h2>Ask HRP</h2>
                        <p className="muted">
                            Answers come from HRP&apos;s rules engine for this plan, not from reading
                            provision text. Add a service code for an exact answer.
                        </p>
                    </div>
                </header>

                <div className="answer-form">
                    <div className="answer-group">
                        <div className="answer-group-label">Question</div>
                        <div className="answer-row">
                            <div className="answer-field answer-field--span-3">
                                <label htmlFor="answer-question">Saved question</label>
                                <Select
                                    inputId="answer-question"
                                    placeholder={questionsLoading ? "Loading questions…" : "Optional — pick a stock question"}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={questionName}
                                    onChange={setQuestionName}
                                    options={questionOptions}
                                    isLoading={questionsLoading}
                                    isClearable
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={selectMenuStyles}
                                />
                                {questionData?.error && (
                                    <span className="answer-hint danger">
                                        Question list unavailable ({questionData.error}). Ad-hoc still works.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="answer-group">
                        <div className="answer-group-label">Service</div>
                        <div className="answer-row">
                            <div className="answer-field">
                                <label htmlFor="answer-service-code">Service code</label>
                                <AsyncPaginate
                                    inputId="answer-service-code"
                                    placeholder="Search CPT / HCPCS"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    debounceTimeout={300}
                                    value={serviceCode}
                                    loadOptions={loadServices}
                                    onChange={setServiceCode}
                                    isClearable
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={selectMenuStyles}
                                />
                            </div>
                            <div className="answer-field">
                                <label htmlFor="answer-pos">Place of service</label>
                                <AsyncPaginate
                                    inputId="answer-pos"
                                    placeholder="Search place of service"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    debounceTimeout={300}
                                    value={placeOfService}
                                    loadOptions={loadPlaceOfService}
                                    onChange={setPlaceOfService}
                                    isClearable
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={selectMenuStyles}
                                />
                            </div>
                            <div className="answer-field">
                                <label htmlFor="answer-claim-type">Claim type</label>
                                <Select
                                    inputId="answer-claim-type"
                                    placeholder="Any"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={claimType}
                                    onChange={setClaimType}
                                    options={CLAIM_TYPE_OPTIONS}
                                    isClearable
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={selectMenuStyles}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="answer-group">
                        <div className="answer-group-label">Member</div>
                        <div className="answer-row">
                            <div className="answer-field">
                                <label htmlFor="answer-member">Member HCC ID</label>
                                <input
                                    id="answer-member"
                                    className="filter-input"
                                    value={memberHccId}
                                    onChange={(e) => setMemberHccId(e.target.value)}
                                    placeholder="Optional — adds used / remaining"
                                />
                            </div>
                            <div className="answer-field">
                                <label htmlFor="answer-pcp">Rendered by PCP</label>
                                <Select
                                    inputId="answer-pcp"
                                    placeholder="Unspecified"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={renderedByPcp}
                                    onChange={setRenderedByPcp}
                                    options={PCP_OPTIONS}
                                    isClearable
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={selectMenuStyles}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="answer-group">
                        <button
                            type="button"
                            className="coverage__link-btn"
                            onClick={() => setAdvancedOpen((open) => !open)}
                            aria-expanded={advancedOpen}
                        >
                            {advancedOpen ? "Hide more criteria" : "More criteria"}
                        </button>
                        {advancedOpen && (
                            <div className="answer-row answer-row--advanced">
                                <div className="answer-field">
                                    <label htmlFor="answer-revenueCode">Revenue code</label>
                                    <CodeSelect
                                        inputId="answer-revenueCode"
                                        value={revenueCode}
                                        onChange={setRevenueCode}
                                        loadOptions={loadRevenueCodes}
                                        placeholder="Search UB revenue code"
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-typeOfBill">Type of bill</label>
                                    <CodeSelect
                                        inputId="answer-typeOfBill"
                                        value={typeOfBill}
                                        onChange={setTypeOfBill}
                                        loadOptions={loadTypeOfBill}
                                        placeholder="Search type of bill"
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-diagnoses">Diagnoses</label>
                                    <CodeSelect
                                        inputId="answer-diagnoses"
                                        value={diagnoses}
                                        onChange={(opts) => setDiagnoses(opts || [])}
                                        loadOptions={loadDiagnosis}
                                        placeholder="Search ICD diagnosis"
                                        isMulti
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-modifiers">Modifiers</label>
                                    <CodeSelect
                                        inputId="answer-modifiers"
                                        value={modifiers}
                                        onChange={(opts) => setModifiers(opts || [])}
                                        loadOptions={loadModifiers}
                                        placeholder="Search modifier"
                                        isMulti
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-procedures">Procedures</label>
                                    <CodeSelect
                                        inputId="answer-procedures"
                                        value={procedures}
                                        onChange={(opts) => setProcedures(opts || [])}
                                        loadOptions={loadProcedureCodes}
                                        placeholder="Search ICD procedure"
                                        isMulti
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-unitCount">Units</label>
                                    <input
                                        id="answer-unitCount"
                                        className="filter-input"
                                        value={criteria.unitCount}
                                        onChange={setField("unitCount")}
                                        placeholder="e.g. 1"
                                        inputMode="numeric"
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-billedAmount">Billed amount</label>
                                    <input
                                        id="answer-billedAmount"
                                        className="filter-input"
                                        value={criteria.billedAmount}
                                        onChange={setField("billedAmount")}
                                        placeholder="e.g. 250"
                                        inputMode="decimal"
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-practitionerHccId">Practitioner HCC ID</label>
                                    <input
                                        id="answer-practitionerHccId"
                                        className="filter-input"
                                        value={criteria.practitionerHccId}
                                        onChange={setField("practitionerHccId")}
                                        placeholder="optional"
                                    />
                                </div>
                                <div className="answer-field">
                                    <label htmlFor="answer-supplierHccId">Supplier HCC ID</label>
                                    <input
                                        id="answer-supplierHccId"
                                        className="filter-input"
                                        value={criteria.supplierHccId}
                                        onChange={setField("supplierHccId")}
                                        placeholder="optional"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="answer-actions">
                    <button
                        type="button"
                        className="btn"
                        onClick={() => runWith()}
                        disabled={answer.isPending || !plan?.name}
                    >
                        {answer.isPending ? "Asking HRP…" : "Get answer"}
                    </button>
                    <button type="button" className="outline-btn" onClick={reset}>
                        Reset
                    </button>
                    <span className="muted answer-context">
                        {plan?.name} · as of {asOf}
                        {fromCategory ? ` · from ${fromCategory}` : ""}
                    </span>
                </div>
            </section>

            {answer.isError && (
                <span className="danger">
                    Error: {answer.error?.message || "Failed to get an answer"}
                </span>
            )}

            {result?.error && <span className="danger">{result.error}</span>}

            {result && !result.error && (
                <section className="overview-block answer-result" ref={answerRef}>
                    <header className="overview-block-head">
                        <h2>What the member pays</h2>
                        <p className="muted">
                            {showBalances
                                ? `Live balances for member ${result.member?.hccId}`
                                : "These are the plan’s listed amounts. Add a member HCC ID to see what is left."}
                        </p>
                    </header>

                    {!!result.errors?.length && (
                        <div className="answer-guidance warn">
                            {result.errors.slice(0, 3).map((e, i) => <p key={i}>{e}</p>)}
                        </div>
                    )}

                    {!tiers.length ? (
                        <p className="muted">
                            Nothing useful came back. Try a service code, or pick a saved question.
                        </p>
                    ) : (
                        <>
                            <div className="table-wrapper answer-glance-wrap">
                                <table className="answer-glance">
                                    <thead>
                                        <tr>
                                            <th>Network</th>
                                            <th>Member pays</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tiers.map((tier, i) => {
                                            const story = memberStory(tier, { askedCode });
                                            const conf = confidence(tier, { askedCode });
                                            return (
                                                <tr key={`${tier.tierName}-${i}`}>
                                                    <td>{networkLabel(tier)}</td>
                                                    <td>{story.headline}</td>
                                                    <td>
                                                        <span className={`badge answer-quality-${conf.key === "sure" ? "exact" : conf.key === "maybe" ? "possible" : "none"}`}>
                                                            {conf.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="answer-tiers">
                                {tiers.map((tier, i) => (
                                    <TierAnswerCard
                                        key={`${tier.tierName}-${i}`}
                                        tier={tier}
                                        showBalances={showBalances}
                                        askedCode={askedCode}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </section>
            )}
        </div>
    );
}

export default CoverageAnswer;
export { CoverageAnswer };
